import React, { useMemo } from 'react';
import { TrackerCatalyst } from '../../types';
import TrackerIcon from './TrackerIcon';
import { addDays, assetKey, evPast, flagged, today } from './helpers';

interface Props {
  rows: TrackerCatalyst[];
  onStat: (stat: 'all' | 'next' | 'flagged' | 'recent') => void;
}

/** The `.stats` row of pill buttons from tracker.js's renderStats(). */
const CatalystStatsBar: React.FC<Props> = ({ rows, onStat }) => {
  const stats = useMemo(() => {
    const TODAY = today();
    const in30 = addDays(TODAY, 30);
    const ago30 = addDays(TODAY, -30);
    const next30 = rows.filter((r) => r.status === 'upcoming' && r.date && r.date >= TODAY && r.date <= in30).length;
    const flaggedCount = rows.filter(flagged).length;
    const recent = rows.filter((r) => r.status === 'past' && r.date && r.date >= ago30 && r.date <= TODAY).length;
    const upcoming = rows.filter((r) => !evPast(r)).length;
    const past = rows.length - upcoming;
    const assets = new Set(rows.map(assetKey)).size;
    const companies = new Set(rows.map((r) => r.company).filter(Boolean)).size;
    return { total: rows.length, upcoming, past, next30, flaggedCount, recent, assets, companies };
  }, [rows]);

  return (
    <div className="stats" aria-label="Summary">
      <button type="button" className="stat" title={`${stats.total} catalysts tracked · ${stats.upcoming} upcoming · ${stats.past} past`} onClick={() => onStat('all')}>
        <TrackerIcon name="cal" /><b>{stats.total}</b>
      </button>
      <button type="button" className="stat" title={`${stats.next30} expected in the next 30 days`} onClick={() => onStat('next')}>
        <TrackerIcon name="upcoming" /><b>{stats.next30}</b>
      </button>
      <button type="button" className={`stat${stats.flaggedCount ? ' warn' : ''}`} title={`${stats.flaggedCount} delayed or overdue`} onClick={() => onStat('flagged')}>
        <TrackerIcon name="alert" /><b>{stats.flaggedCount}</b>
      </button>
      <button type="button" className="stat" title={`${stats.recent} reported in the last 30 days`} onClick={() => onStat('recent')}>
        <TrackerIcon name="history" /><b>{stats.recent}</b>
      </button>
      <button type="button" className="stat" title={`${stats.assets} assets across ${stats.companies} companies`} onClick={() => onStat('all')}>
        <TrackerIcon name="building" /><b>{stats.assets}</b><span className="sep">/</span><b>{stats.companies}</b>
      </button>
    </div>
  );
};

export default CatalystStatsBar;
